/**
 * Lightweight demo-workflow mode (Onboarding 1C).
 *
 * Adds the feature flag, the `#/demo` route helper, and a tiny local-only
 * "demo seen" preference for the signed-in, no-Google "Try it in 60 seconds"
 * demo described in docs/DEMO_MODE_INVESTIGATION_AND_PLAN.md.
 *
 * IMPORTANT: This is OFF by default. The demo entry/route only activate when
 * BOTH `VITE_APP_SHELL` and `VITE_DEMO_WORKFLOW` are explicitly `true` and the
 * user is signed in. Any other value (including unset) is OFF, so default
 * production behavior is unchanged.
 *
 * This module is intentionally dependency-free (only the `AppPhase` enum) so it
 * can never pull in scan/grading/Google/export/persistence code.
 */

import { AppPhase } from '../types';

function readDemoWorkflowEnv(): boolean {
  return import.meta.env.VITE_DEMO_WORKFLOW === 'true';
}

/** True only when `VITE_DEMO_WORKFLOW` is explicitly enabled. Defaults OFF. */
export const DEMO_WORKFLOW_ENABLED = readDemoWorkflowEnv();

/** Returns the demo-workflow feature flag value. */
export function isDemoWorkflowEnabled(): boolean {
  return DEMO_WORKFLOW_ENABLED;
}

/** Canonical hash route for the demo workflow. */
export const DEMO_WORKFLOW_HASH = '#/demo';

/** The phase the demo workflow renders under. */
export const DEMO_WORKFLOW_PHASE = AppPhase.DEMO_WORKFLOW;

function normalizeHash(hash: string): string {
  if (!hash) return '';
  const trimmed = hash.trim();
  const withHash = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  return withHash.length > 2 && withHash.endsWith('/') ? withHash.slice(0, -1) : withHash;
}

/** True when `hash` is the demo route (tolerates missing "#" / trailing slash). */
export function isDemoWorkflowHash(hash: string): boolean {
  return normalizeHash(hash) === DEMO_WORKFLOW_HASH;
}

/**
 * Local-only "the teacher has opened the demo at least once" flag. Stores ONLY
 * the literal string "true" — never any demo/student content. Used to let Home
 * gently change its nudge after a demo. Optional; the demo works without it.
 */
export const DEMO_SEEN_KEY = 'dg_demo_seen_v1';
const DEMO_SEEN_VALUE = 'true';

/**
 * True only when the teacher has opened the demo before on this device. Any
 * storage error (unavailable / blocked) is swallowed and treated as "not seen"
 * so the demo never crashes the app.
 */
export function hasSeenDemo(): boolean {
  try {
    return globalThis.localStorage?.getItem(DEMO_SEEN_KEY) === DEMO_SEEN_VALUE;
  } catch {
    return false;
  }
}

/**
 * Persist the "demo seen" preference locally. Best-effort: if storage is
 * unavailable the call is a no-op (the demo still works for this session).
 */
export function markDemoSeen(): void {
  try {
    globalThis.localStorage?.setItem(DEMO_SEEN_KEY, DEMO_SEEN_VALUE);
  } catch {
    /* storage unavailable — keep going, the app must not break */
  }
}
